import React, { useState } from 'react';
import { Calendar, User, Clock, ArrowLeft, Share2, Twitter, Linkedin, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─── FAQ Accordion ─────────────────────────────────────── */
const faqs = [
  {
    q: 'What is urban air pollution and why does it matter?',
    a: 'Urban air pollution refers to the contamination of city air by harmful substances like exhaust fumes, dust, and chemical pollutants, primarily from vehicles and industry. It matters because millions of people breathe this air daily, often without realising the long-term damage it causes to their health.',
  },
  {
    q: 'What are the main causes of urban pollution in India?',
    a: 'Vehicular emissions are the single biggest cause of urban pollution in Indian cities. Every petrol auto, cab, and bike on the road releases carbon monoxide, nitrogen oxides, and particulate matter. Construction dust, industrial discharge, and waste burning also contribute significantly.',
  },
  {
    q: 'How do petrol vehicles contribute to urban air pollution?',
    a: 'It would take nearly 200 trees just to offset the emissions produced by one average petrol vehicle in a year. With millions of petrol vehicles on Indian roads, the cumulative effect on city air quality is enormous, particularly during morning and evening rush hours.',
  },
  {
    q: 'Why do people not react more strongly to urban pollution?',
    a: 'Because exposure is gradual. When pollution increases slowly over years, people adjust their baseline of what feels normal. Polluted air stops feeling alarming and starts feeling familiar, which is precisely what makes it so dangerous.',
  },
  {
    q: 'Can one person\'s commute choice actually make a difference?',
    a: 'Yes. When millions of individuals each believe their choice does not matter, the collective result is a city that never changes. Switching to an electric auto for your daily commute is one of the most direct, consistent ways to reduce your personal carbon footprint in Chennai.',
  },
  {
    q: 'What is Nura and how does it help reduce air pollution in Chennai?',
    a: 'Nura is an electric mobility company operating in Chennai. Every Nura auto is fully electric, which means zero tailpipe emissions on every trip. With 25,000+ people already using Nura in Chennai, the collective impact on urban air quality is real and measurable.',
  },
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-white/5 transition-colors"
        aria-expanded={open}
      >
        <span className="text-white font-semibold pr-4">{faq.q}</span>
        {open
          ? <ChevronUp size={18} className="text-[#bceb39] flex-shrink-0" />
          : <ChevronDown size={18} className="text-[#bceb39] flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-5 text-gray-300 text-base leading-relaxed border-t border-white/10 pt-4">
          {faq.a}
        </div>
      )}
    </div>
  );
}

/* ─── Main Section ───────────────────────────────────────── */
const UrbanPollutionSection = () => {
  const navigate = useNavigate();

  return (
    <article
      className="min-h-screen bg-black text-white"
      itemScope
      itemType="https://schema.org/Article"
    >

      {/* ── Hero Header (no image) ── */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <span className="inline-block bg-[#bceb39] text-black text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
          Sustainability
        </span>
        {/* H1 — exact meta title as provided */}
        <h1
          className="text-4xl md:text-5xl font-bold leading-tight text-white"
          itemProp="headline"
        >
          Why Does the Morning Air Feel Different? The Truth About Urban Pollution
        </h1>
      </div>

      {/* ── Meta Bar ── */}
      <div className="max-w-4xl mx-auto px-6 py-5 border-y border-gray-800 flex flex-wrap gap-6 items-center justify-between mb-4">
        <div className="flex flex-wrap gap-5 text-sm text-gray-400">
          <span
            className="flex items-center gap-2"
            itemProp="author"
            itemScope
            itemType="https://schema.org/Person"
          >
            <User size={15} className="text-[#bceb39]" />
            <span itemProp="name">Nura Team</span>
          </span>
          <span className="flex items-center gap-2">
            <Calendar size={15} className="text-[#bceb39]" />
            <time itemProp="datePublished" dateTime="2025-04-22">April 22, 2025</time>
          </span>
          <span className="flex items-center gap-2">
            <Clock size={15} className="text-[#bceb39]" />
            5 min read
          </span>
        </div>
        <div className="flex items-center gap-3 text-gray-400 text-sm">
          <Share2 size={15} className="text-[#bceb39]" />
          <span>Share:</span>
          <a
            href="https://twitter.com/intent/tweet?text=Why+Does+the+Morning+Air+Feel+Different%3F&url=https://nura.app/blog/urban-pollution"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Twitter"
            className="hover:text-[#bceb39] transition-colors"
          >
            <Twitter size={17} />
          </a>
          <a
            href="https://www.linkedin.com/sharing/share-offsite/?url=https://nura.app/blog/urban-pollution"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            className="hover:text-[#bceb39] transition-colors"
          >
            <Linkedin size={17} />
          </a>
        </div>
      </div>

      {/* ── Article Body ── */}
      <div
        className="max-w-4xl mx-auto px-6 py-10 space-y-12 text-gray-300 leading-relaxed text-lg"
        itemProp="articleBody"
      >

        {/* ── SECTION 1 ── */}
        <section aria-labelledby="section-normal">
          <h2
            id="section-normal"
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            We Walk Through It Every Day and Call It Normal
          </h2>
          <p className="mb-4">
            Step outside on any given morning anywhere in the world.
          </p>
          <p className="mb-4">
            The first thing many of us breathe every morning isn't fresh air. The air is heavy
            before the day even begins. It's a mixture of exhaust fumes, dust, and invisible
            pollutants released by millions of vehicles long before the city is fully awake. We
            inhale it without a second thought, because it feels familiar. Somewhere along the
            way, polluted air stopped feeling alarming and started feeling normal.
          </p>
          <p className="font-semibold text-white">
            It isn't normal. It never was.
          </p>
        </section>

        {/* ── SECTION 2 ── */}
        <section aria-labelledby="section-problem">
          <h2
            id="section-problem"
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            The Problem With "Normal"
          </h2>
          <p className="mb-4">
            The planet is warming at a rate it hasn't seen in thousands of years.
          </p>
          <p className="mb-4">
            The answer, if you look for it, is idling right outside your window every morning.
            Think about the sheer volume of rides happening right now across Chennai alone, every
            Rapido bike, every Ola and Uber cab, every traditional petrol auto navigating the
            morning rush. Each one of them simply doing their job but that happens to cost the air
            something every single time.
          </p>
          {/* Keyword-rich stat callout — original text, no rewrite */}
          <div className="border-l-4 border-[#bceb39] pl-6 py-3 bg-white/5 rounded-r-xl my-6">
            <p className="text-white font-medium">
              It would take nearly{' '}
              <span className="text-[#bceb39] font-bold">200 trees</span> just to offset the
              emissions produced by one average petrol vehicle in a year. Now imagine millions of
              petrol vehicles on Indian roads right now, each one adding to the air we breathe
              every day.
            </p>
          </div>
        </section>

        {/* ── SECTION 3 ── */}
        <section aria-labelledby="section-comfort">
          <h2
            id="section-comfort"
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            The Dangerous Comfort of Not Knowing
          </h2>
          <p className="mb-4">
            Here is the truth nobody puts on a billboard. The planet is not slowly getting worse.
            It already got worse. We just didn't notice because it happened gradually enough for
            us to keep adjusting what we call normal. The most dangerous thing about the
            environmental crisis isn't the scale of it. It's how thoroughly we have learned to
            live alongside it without flinching.
          </p>
          <p>
            Every day we treat polluted air as background noise, every morning we sit in
            exhaust-heavy traffic without a second thought, we are quietly voting for the world to
            stay exactly as it is. The world cannot afford that vote much longer.
          </p>
        </section>

        {/* ── SECTION 4 ── */}
        <section aria-labelledby="section-excuse">
          <h2
            id="section-excuse"
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            The Excuse We've All Used
          </h2>
          <p className="mb-4 italic text-gray-400 text-xl border-l-4 border-white/20 pl-5">
            "I'm just one person. What difference does it make?"
          </p>
          <p className="mb-4">
            It's an understandable thought. The scale of environmental damage feels so enormous
            that individual action can seem almost embarrassingly small in comparison. But here's
            the thing about that logic, it's the same thought being had by millions of people
            simultaneously. And collectively, that thinking is exactly what allows the problem to
            keep growing undisturbed. Behind every polluted city is a million people who wanted
            better, they just never had better to choose from.
          </p>
        </section>

        {/* ── SECTION 5 ── */}
        <section aria-labelledby="section-chennai">
          <h2
            id="section-chennai"
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Chennai Is Already Answering That Question
          </h2>
          <p className="mb-4">
            This is where NURA comes in, not with a lecture, but with a different option. Every
            NURA auto is electric which means every trip is one that Chennai's air doesn't have
            to pay for.
          </p>
          <p className="mb-4">
            Environmental progress was never in the hands of corporations. It was always in yours.
            Your morning commute is one of those moments. It is one of the most consistent ones.
            Because you make it every single day and it is another chance for that choice to
            either add to the problem or quietly chip away at it.
          </p>

          {/* Stat callout */}
          <div className="bg-[#bceb39]/10 border border-[#bceb39]/30 rounded-2xl px-8 py-8 my-8 text-center">
            <p className="text-5xl font-bold text-[#bceb39] mb-2">25,000+</p>
            <p className="text-white font-medium text-lg">people in Chennai already started.</p>
          </div>

          <p className="mb-4">
            This is what a new era of mobility looks like. It's happening right now on Chennai's
            streets, in the form of a quiet electric auto that shows up, takes you where you need
            to go, and leaves the air exactly as it found it.
          </p>
          <p className="mb-4">
            That's what NURA means. Literally.
          </p>
          <p className="mb-6 text-lg">
            New + Era ={' '}
            <a
              href="/"
              className="font-bold italic text-[#bceb39] hover:underline transition-colors"
            >
              NURA
            </a>.
          </p>
          <p className="text-xl font-bold text-white">
            Choose better. Every day.
          </p>
        </section>

        {/* ── INTERNAL LINKS ── */}
        <section
          aria-labelledby="section-explore"
          className="bg-white/5 border border-white/10 rounded-2xl p-8"
        >
          <h2 id="section-explore" className="text-xl font-bold text-white mb-5">
            Explore More From Nura
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Our Electric Fleet', desc: "See how Nura's electric autos work", href: '/services' },
              { label: 'Drive With Us', desc: 'Become a Nura pilot in Chennai', href: '/drive-with-us' },
              { label: 'Our Safety Promise', desc: 'How we keep every ride safe', href: '/safety' },
            ].map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block bg-black border border-white/10 rounded-xl px-5 py-4 hover:border-[#bceb39] transition-colors group"
              >
                <p className="text-white font-semibold group-hover:text-[#bceb39] transition-colors">
                  {link.label} &rarr;
                </p>
                <p className="text-gray-400 text-sm mt-1">{link.desc}</p>
              </a>
            ))}
          </div>

          {/* External links */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">
              Further Reading
            </h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'WHO: Ambient Air Quality & Health', href: 'https://www.who.int/news-room/fact-sheets/detail/ambient-(outdoor)-air-quality-and-health' },
                { label: 'IQAir India Air Quality Report', href: 'https://www.iqair.com/india' },
                { label: "India's FAME II EV Scheme", href: 'https://fame2.heavyindustry.gov.in/' },
                { label: 'Centre for Science and Environment', href: 'https://www.cseindia.org/' },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#bceb39] text-sm underline hover:opacity-75 transition-opacity"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ SECTION ── */}
        <section aria-labelledby="section-faq">
          <h2
            id="section-faq"
            className="text-2xl md:text-3xl font-bold text-white mb-2"
          >
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 mb-8 text-base">
            Common questions about urban air pollution in India and electric mobility solutions in Chennai.
          </p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} />
            ))}
          </div>
        </section>

        {/* ── TAGS ── */}
        <div className="pt-2 flex flex-wrap gap-2">
          {[
            'Chennai', 'AirPollution', 'WorldEarthDay', 'ElectricAuto',
            'OlaCab', 'ElectricVehicle', 'Sustainability', 'CarbonFootprint',
          ].map((tag) => (
            <span
              key={tag}
              className="bg-white/5 border border-white/10 text-gray-400 text-xs px-3 py-1.5 rounded-full hover:border-[#bceb39] hover:text-[#bceb39] transition-colors cursor-default"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* ── CTA ── */}
        <div className="bg-[#bceb39] rounded-2xl p-8 md:p-12 text-center">
          <p className="text-black text-xs font-bold uppercase tracking-widest mb-3">
            Electric Auto Rides in Chennai
          </p>
          <h2 className="text-black text-2xl md:text-3xl font-bold mb-3 leading-tight">
            Your commute can be part of the solution.
          </h2>
          <p className="text-black/70 mb-6 text-base max-w-xl mx-auto">
            Switch to a Nura electric auto. Zero emissions, fixed fares, zero cancellations.
            25,000+ people in Chennai already started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.nuraemobility.co.in/?c=YImSUF"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-black text-[#bceb39] font-bold px-8 py-3 rounded-full hover:scale-105 transition-transform"
            >
              Download on Google Play &rarr;
            </a>
            <a
              href="https://app.nuraemobility.co.in/?c=YImSUF"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-black text-[#bceb39] font-bold px-8 py-3 rounded-full hover:scale-105 transition-transform"
            >
              Download on App Store &rarr;
            </a>
          </div>
        </div>

      </div>

      {/* ── Back Button ── */}
      <div className="max-w-4xl mx-auto px-6 pb-16">
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#bceb39] transition-colors text-sm font-medium"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </button>
      </div>

    </article>
  );
};

export default UrbanPollutionSection;
